import React, {useCallback, useState} from 'react';
import { FormSearch } from 'grommet-icons';
import { Select } from 'grommet';
import {useMapsLibrary} from '@vis.gl/react-google-maps';
import {useAutocompleteSuggestions} from './useAutoCompleteSuggestions';

interface Props {
  onPlaceSelect: (place: google.maps.places.Place | null) => void;
  defaultValue?: string;
}

export const AutocompleteCustom = ({onPlaceSelect, defaultValue}: Props) => {
  const places = useMapsLibrary('places');

  const [inputValue, setInputValue] = useState<string>(defaultValue || '');
  const {suggestions, resetSession} = useAutocompleteSuggestions(inputValue);

  const handleInput = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  const handleSuggestionClick = useCallback(
    async (suggestion: google.maps.places.AutocompleteSuggestion) => {
      if (!places) return;
      if (!suggestion.placePrediction) return;

      const place = suggestion.placePrediction.toPlace();

      await place.fetchFields({
        fields: [
          'location',
          'addressComponents',
          'formattedAddress',
        ]
      });

      setInputValue(suggestion.placePrediction.text.text);

      // calling fetchFields invalidates the session-token, so we now have to call
      // resetSession() so a new one gets created for further search
      resetSession();

      onPlaceSelect(place);
    },
    [places, onPlaceSelect, resetSession]
  );

  return (
    <Select
      options={suggestions.map(suggestion => ({
        label: suggestion.placePrediction?.text.text || '',
        value: suggestion
      }))}
      onChange={({ option }) => handleSuggestionClick(option.value)}
      value={inputValue}
      onSearch={event => handleInput(event)}
      icon={<FormSearch />}
      size="small"
      placeholder={inputValue || 'Search for a location'}
    />
  );
};
